import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell, Envelope, EnvelopeOpen, Trash, CheckCircle, PaperPlaneTilt,
  Warning, Calendar, ClipboardText, FileText, ShieldCheck, ChatCircle, Lightning,
} from "@phosphor-icons/react";

const TYPE_META = {
  writeup:    { color: "bg-red-100 text-red-800 border-red-300",        Icon: ShieldCheck,    label: "WRITE-UP" },
  incident:   { color: "bg-amber-100 text-amber-800 border-amber-300",  Icon: Warning,        label: "INCIDENT" },
  supply:     { color: "bg-blue-100 text-blue-800 border-blue-300",     Icon: ClipboardText,  label: "SUPPLY" },
  drop:       { color: "bg-orange-100 text-orange-800 border-orange-300", Icon: Calendar,     label: "DROP" },
  trade:      { color: "bg-teal-100 text-teal-800 border-teal-300",     Icon: Calendar,       label: "TRADE" },
  document:   { color: "bg-purple-100 text-purple-800 border-purple-300", Icon: FileText,     label: "DOCUMENT" },
  compliance: { color: "bg-indigo-100 text-indigo-800 border-indigo-300", Icon: ShieldCheck,  label: "COMPLIANCE" },
  message:    { color: "bg-slate-100 text-slate-800 border-slate-300",  Icon: ChatCircle,     label: "MESSAGE" },
  general:    { color: "bg-slate-100 text-slate-800 border-slate-300",  Icon: Bell,           label: "ALERT" },
};

export default function Inbox() {
  const { user } = useAuth();
  const { unread, refresh: refreshUnread, setUnread } = useNotifications();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | unread
  const [busy, setBusy] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  // Full-message viewer — opens on row click. `null` closes the dialog.
  const [active, setActive] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);

  async function load() {
    const { data } = await api.get("/notifications/", { params: filter === "unread" ? { unread_only: true } : {} });
    setItems(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Re-poll when unread count changes (means a new push arrived).
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unread]);

  async function markRead(n) {
    if (!n || n.read) return;
    await api.post(`/notifications/${n.id}/read`);
    setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
    refreshUnread();
  }
  async function markAllRead() {
    setBusy(true);
    try {
      await api.post("/notifications/read-all");
      await load();
      setUnread(0);
      toast.success("All marked as read");
    } finally { setBusy(false); }
  }
  async function remove(n) {
    await api.delete(`/notifications/${n.id}`);
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.read) refreshUnread();
    if (active?.id === n.id) setActive(null);
  }
  function openItem(n) {
    // Open the full message viewer. Marking-as-read happens inside the
    // dialog so a cold-open from a push notification still updates the
    // unread badge. The viewer owns navigation to related pages via
    // the "Go to related page" button — we no longer auto-navigate on
    // row click because that hid the message body entirely.
    setActive({ ...n });
    markRead(n);
  }

  const unreadCount = items.filter((x) => !x.read).length;

  return (
    <AppLayout title="Inbox" subtitle={`${unreadCount} unread • real-time alerts for trades, drops, write-ups & more`} actions={
      <div className="flex items-center gap-2" data-testid="inbox-actions">
        <div className="border border-slate-200 rounded-sm overflow-hidden flex">
          <button onClick={() => setFilter("all")} className={`px-3 py-1.5 text-xs font-semibold ${filter === "all" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid="filter-all">ALL ({items.length})</button>
          <button onClick={() => setFilter("unread")} className={`px-3 py-1.5 text-xs font-semibold ${filter === "unread" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`} data-testid="filter-unread">UNREAD</button>
        </div>
        <Button variant="outline" onClick={markAllRead} disabled={busy || unreadCount === 0} className="rounded-sm h-9 text-xs" data-testid="btn-mark-all">
          <CheckCircle size={14} weight="bold" className="mr-1" />MARK ALL READ
        </Button>
        <ComposeDialog open={composeOpen} setOpen={setComposeOpen} currentUser={user} onSent={load} />
      </div>
    }>
      <div className="surface-grid">
        {items.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Bell size={36} className="mx-auto opacity-30 mb-2" />
            <div className="font-display text-lg">No notifications</div>
            <div className="text-xs">Updates from trades, drops, write-ups and supply requests will appear here in real time.</div>
          </div>
        ) : items.map((n) => {
          const meta = TYPE_META[n.type] || TYPE_META.general;
          const I = meta.Icon;
          return (
            <div key={n.id} onClick={() => openItem(n)}
              className={`border-b border-slate-100 px-4 py-3 cursor-pointer transition-colors flex items-start gap-3 ${n.read ? "bg-white" : "bg-emerald-50/30 hover:bg-emerald-50/60"}`}
              data-testid={`inbox-row-${n.id}`}>
              <div className={`p-2 rounded-sm border ${meta.color} shrink-0`} aria-hidden>
                <I size={16} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {!n.read && <Lightning size={12} weight="fill" className="text-emerald-600 shrink-0" />}
                  <Badge className={`rounded-sm text-[10px] border ${meta.color}`}>{meta.label}</Badge>
                  <span className={`font-medium text-sm ${n.read ? "text-slate-700" : "text-slate-900"}`}>{n.title}</span>
                  <span className="tab-tag text-slate-400 ml-auto mono text-[10px]">{new Date(n.created_at).toLocaleString()}</span>
                </div>
                {n.body && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{n.body}</div>}
                {n.sender_name && <div className="tab-tag text-slate-500 mt-1">FROM: {n.sender_name}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {n.read ? <EnvelopeOpen size={16} className="text-slate-400" /> : <Envelope size={16} weight="fill" className="text-emerald-600" />}
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                  onClick={(e) => { e.stopPropagation(); remove(n); }}
                  data-testid={`inbox-del-${n.id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <MessageViewer
        n={active}
        onClose={() => setActive(null)}
        onDelete={remove}
        onReply={() => { setReplyOpen(true); }}
        onNavigate={(link) => { setActive(null); nav(link); }}
      />
      {active && (
        <ComposeDialog
          open={replyOpen}
          setOpen={setReplyOpen}
          currentUser={user}
          onSent={() => { setReplyOpen(false); load(); }}
          replyTo={active}
          hideTrigger
        />
      )}
    </AppLayout>
  );
}

function MessageViewer({ n, onClose, onDelete, onReply, onNavigate }) {
  if (!n) return null;
  const meta = TYPE_META[n.type] || TYPE_META.general;
  const I = meta.Icon;
  const canReply = Boolean(n.sender_id);
  return (
    <Dialog open={!!n} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl p-0 overflow-hidden" data-testid="inbox-viewer">
        <DialogHeader className="px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`p-2 rounded-sm border ${meta.color}`} aria-hidden>
              <I size={16} weight="bold" />
            </div>
            <Badge className={`rounded-sm text-[10px] border ${meta.color}`}>{meta.label}</Badge>
            <span className="mono text-[10px] text-slate-500 ml-auto">
              {new Date(n.created_at).toLocaleString()}
            </span>
          </div>
          <DialogTitle className="font-display text-xl mt-2 leading-tight">{n.title}</DialogTitle>
          {n.sender_name && (
            <div className="tab-tag text-slate-500 mt-1">FROM: {n.sender_name}</div>
          )}
        </DialogHeader>
        <div className="px-5 py-4 max-h-[55vh] overflow-y-auto" data-testid="inbox-viewer-body">
          {n.body ? (
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{n.body}</p>
          ) : (
            <p className="text-sm text-slate-500 italic">No additional details.</p>
          )}
        </div>
        <DialogFooter className="px-5 py-3 border-t border-slate-200 gap-2 bg-slate-50 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-sm text-red-600 hover:bg-red-50 mr-auto"
            onClick={() => onDelete(n)}
            data-testid="inbox-viewer-delete"
          >
            <Trash size={14} className="mr-1" /> Delete
          </Button>
          {canReply && (
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={onReply}
              data-testid="inbox-viewer-reply"
            >
              <PaperPlaneTilt size={14} className="mr-1" /> Reply
            </Button>
          )}
          {n.link && (
            <Button
              size="sm"
              className="rounded-sm bg-slate-900 text-white hover:bg-slate-800"
              onClick={() => onNavigate(n.link)}
              data-testid="inbox-viewer-go"
            >
              Open related page
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={onClose}
            data-testid="inbox-viewer-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ComposeDialog({ open, setOpen, currentUser, onSent, replyTo = null, hideTrigger = false }) {
  const [users, setUsers] = useState([]);
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) {
      api.get("/users/").then((r) => setUsers(r.data)).catch(() => setUsers([]));
      if (replyTo) {
        // Pre-fill a reply — lock the recipient to the original sender
        // and seed the subject with Re: + original title so threaded
        // context carries across even on a simple notification table.
        setRecipient(replyTo.sender_id || "");
        setSubject(replyTo.title?.toLowerCase().startsWith("re:") ? replyTo.title : `Re: ${replyTo.title || ""}`);
        setBody("");
        setQ(replyTo.sender_name || "");
      } else {
        setRecipient(""); setSubject(""); setBody(""); setQ("");
      }
    }
  }, [open, replyTo]);

  // Restrict low-tier users (volunteers — which BSVAC uses as the umbrella
  // for EMT / Paramedic / MVO / Intern / Student) to messaging supervisor+
  // only. Higher roles can message anyone.
  const LOW_TIER = new Set(["volunteer"]);
  const HIGH_TIER = new Set(["supervisor", "admin", "commanding_officer", "staff_chief", "super_admin"]);
  const eligible = users.filter((u) => {
    if (u.id === currentUser?.id) return false;
    if (LOW_TIER.has(currentUser?.role)) return HIGH_TIER.has(u.role);
    return true;
  });
  const needle = q.trim().toLowerCase();
  const searched = needle
    ? eligible.filter((u) => `${u.name} ${u.role} ${(u.certifications || []).join(" ")}`.toLowerCase().includes(needle))
    : eligible;

  async function send(e) {
    e.preventDefault();
    if (!recipient || !subject.trim() || !body.trim()) { toast.error("All fields required"); return; }
    try {
      await api.post("/notifications/message", { recipient_id: recipient, subject: subject.trim(), body: body.trim() });
      toast.success("Message sent");
      setOpen(false);
      onSent && onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button className="rounded-sm bg-slate-900 text-white" data-testid="btn-compose">
            <PaperPlaneTilt size={14} weight="bold" className="mr-1" />COMPOSE
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader><DialogTitle>{replyTo ? "Reply" : "New Message"}</DialogTitle></DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <div>
            <Label className="tab-tag">To</Label>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, role, or certification…"
              className="rounded-sm mb-1"
              data-testid="msg-to-search"
            />
            <select value={recipient} onChange={(e) => setRecipient(e.target.value)} className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm bg-white" data-testid="msg-to">
              <option value="">— Select —</option>
              {searched.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role}</option>)}
            </select>
            {currentUser?.role === "volunteer" && (
              <p className="text-[11px] text-slate-500 mt-1">Volunteers, EMTs, Paramedics, MVOs, Interns and Students may only message supervisors, admins, staff chiefs, commanding officer or super admin.</p>
            )}
          </div>
          <div><Label className="tab-tag">Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-sm" data-testid="msg-subject" /></div>
          <div><Label className="tab-tag">Message</Label><Textarea value={body} onChange={(e) => setBody(e.target.value)} className="rounded-sm h-28" data-testid="msg-body" /></div>
          <DialogFooter>
            <Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="msg-send">
              <PaperPlaneTilt size={14} className="mr-1" />Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
